import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import {
    HelpCircle, BookOpenCheck, Upload, Calculator, Sparkles,
    LayoutDashboard, ListTree, FileBarChart, Snowflake,
    FileSpreadsheet, UsersRound, DatabaseBackup,
    Lightbulb, ChevronRight, KeyRound, ShieldCheck,
    ArrowLeftRight, PackageSearch, Wrench,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Kapitel-Konfiguration — anpassen um Inhaltsverzeichnis + Anker zu erweitern.
// -----------------------------------------------------------------------------
const CHAPTERS = [
    { id: "grundlagen",    label: "1. Grundlagen",              icon: BookOpenCheck },
    { id: "einstieg",      label: "2. Einstieg & Login",        icon: KeyRound },
    { id: "verlagswahl",   label: "3. Verlagsauswahl",          icon: LayoutDashboard },
    { id: "uebersicht",    label: "4. Übersicht (Dashboard)",   icon: LayoutDashboard },
    { id: "import",        label: "5. Excel-Import",            icon: Upload },
    { id: "inventar",      label: "6. Inventar & Titelpflege",  icon: ListTree },
    { id: "bewertung",     label: "7. Bewertung & WB-Staffel",  icon: Calculator },
    { id: "sonder-wb",     label: "8. Sonder-Wertberichtigung", icon: PackageSearch },
    { id: "berichte",      label: "9. Berichte & Tags",         icon: FileBarChart },
    { id: "snapshots",     label: "10. Snapshots (Jahresabschluss)", icon: Snowflake },
    { id: "export",        label: "11. Export für Buchhaltung", icon: FileSpreadsheet },
    { id: "benutzer",      label: "12. Benutzerverwaltung",     icon: UsersRound },
    { id: "backup",        label: "13. Backup, Restore & Reset", icon: DatabaseBackup },
    { id: "tipps",         label: "14. Tipps & Troubleshooting", icon: Wrench },
];

// -----------------------------------------------------------------------------
// Kleine Hilfskomponenten
// -----------------------------------------------------------------------------
function Section({ id, icon: Icon, title, children }) {
    return (
        <section id={id} className="scroll-mt-24">
            <Card className="p-6 sm:p-7 bg-surface1 border-border">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" /> Kapitel
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">{title}</h2>
                <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground">
                    {children}
                </div>
            </Card>
        </section>
    );
}

function Tip({ children, tone = "info" }) {
    const tones = {
        info: "border-blue-500/40 bg-blue-500/10 text-blue-100",
        warn: "border-yellow-500/40 bg-yellow-500/10 text-yellow-100",
        good: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100",
    };
    return (
        <div className={`rounded-md border px-3.5 py-2.5 text-[13px] ${tones[tone]}`}>
            <div className="flex items-start gap-2">
                <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-80" />
                <div>{children}</div>
            </div>
        </div>
    );
}

function KBD({ children }) {
    return (
        <kbd className="inline-flex items-center rounded border border-border bg-surface2 px-1.5 py-0.5 text-[11px] font-mono text-foreground">
            {children}
        </kbd>
    );
}

function Steps({ items }) {
    return (
        <ol className="space-y-2.5 list-decimal pl-5 marker:text-tenant">
            {items.map((it, i) => (
                <li key={i} className="text-muted-foreground">
                    {typeof it === "string" ? it : (
                        <>
                            <span className="text-foreground font-medium">{it.title}</span>
                            {it.desc && <> — {it.desc}</>}
                        </>
                    )}
                </li>
            ))}
        </ol>
    );
}

function Term({ term, def }) {
    return (
        <div className="grid sm:grid-cols-[180px,1fr] gap-2 py-2 border-t border-border/60 first:border-t-0">
            <div className="text-foreground font-medium">{term}</div>
            <div>{def}</div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Haupt-Komponente
// -----------------------------------------------------------------------------
export default function Help() {
    const [active, setActive] = useState(CHAPTERS[0].id);

    // Scroll-Spy: markiert das gerade sichtbare Kapitel im Inhaltsverzeichnis
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
        );
        CHAPTERS.forEach((c) => {
            const el = document.getElementById(c.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    return (
        <div className="pb-16 animate-fade-up">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Hilfe</div>
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold mt-1">
                    Handbuch <span className="text-tenant">INVENTUR PRO</span>
                </h1>
                <p className="mt-3 text-muted-foreground max-w-2xl">
                    Ein vollständiges Nachschlagewerk für alle Funktionen. Für den Schnelleinstieg starten Sie
                    mit <a href="#einstieg" className="text-tenant underline underline-offset-2">Kapitel 2</a>, für den Erst-Import
                    mit <a href="#import" className="text-tenant underline underline-offset-2">Kapitel 5</a>.
                </p>
            </div>

            <div className="grid lg:grid-cols-[240px,1fr] gap-8">
                {/* Inhaltsverzeichnis (sticky) */}
                <aside className="hidden lg:block">
                    <div className="sticky top-24 space-y-1" data-testid="help-toc">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mb-2 px-2">
                            Inhaltsverzeichnis
                        </div>
                        {CHAPTERS.map((c) => {
                            const Icon = c.icon;
                            const isActive = active === c.id;
                            return (
                                <a
                                    key={c.id}
                                    href={`#${c.id}`}
                                    data-testid={`help-toc-${c.id}`}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors
                                        ${isActive ? "bg-tenant/15 text-tenant font-medium" : "text-muted-foreground hover:text-foreground hover:bg-surface2"}
                                    `}
                                >
                                    <Icon className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{c.label}</span>
                                    {isActive && <ChevronRight className="h-3 w-3 ml-auto opacity-70" />}
                                </a>
                            );
                        })}
                    </div>
                </aside>

                {/* Kapitel-Content */}
                <div className="space-y-6 max-w-3xl">

                    <Section id="grundlagen" icon={BookOpenCheck} title="Grundlagen">
                        <p>
                            <strong className="text-foreground">INVENTUR PRO</strong> ist eine mandantenfähige Web-Applikation zur
                            jährlichen und periodischen Lagerbewertung (Inventurbewertung) von Buchbeständen. Sie unterstützt
                            mehrere Verlage parallel, hält deren Daten strikt getrennt und berechnet den Bilanzwert nach den
                            Regeln der Wertberichtigungs-Staffel — vollständig auf Deutsch.
                        </p>
                        <div className="pt-2">
                            <div className="text-foreground font-medium mb-2">Kernbegriffe</div>
                            <Term term="Verlag / Mandant" def="Ein eigenständiger Datenraum mit separaten Titeln, Bewegungen und Snapshots. Wechsel jederzeit über die Topbar." />
                            <Term term="Titel (Stammdaten)" def="Ein Buch, identifiziert über die ISBN. Enthält Erlösgruppe, Erstauflagen-Datum (ET), Auflagennummern, Herstellkosten (HK), Programmhalbjahr." />
                            <Term term="Bewegung" def="Ein Bestand zu einem bestimmten Zeitraum (Jahr/Quartal/Monat), inkl. Lager Ö + Lager D, HK Ist/Kal und daraus abgeleiteten Werten." />
                            <Term term="Bewertungszeitraum" def="Jahr, für das die Bilanz berechnet wird — meist das laufende Kalenderjahr (z. B. 2025)." />
                            <Term term="Snapshot" def="Eingefrorene Zusammenfassung eines Jahres für den Jahresabschluss. Nach Abschluss unveränderlich." />
                            <Term term="WB-Staffel" def="Konfigurierbare Wertberichtigungs-Prozente je nach Alter des Titels (Beispiel: 1. Jahr 0 %, 2. Jahr 25 %, …, ab 5. Jahr 100 %)." />
                        </div>
                    </Section>

                    <Section id="einstieg" icon={KeyRound} title="Einstieg & Login">
                        <p>Nach Erhalt Ihrer Zugangsdaten (E-Mail + Initial-Passwort) melden Sie sich unter der Login-Seite an.</p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Erst-Login-Ablauf</div>
                            <Steps items={[
                                { title: "E-Mail und Initial-Passwort eingeben", desc: "auf der Login-Seite." },
                                { title: "Erzwungener Passwortwechsel", desc: "beim allerersten Login erscheint ein Dialog, in dem Sie ein eigenes Passwort setzen müssen (mind. 10 Zeichen, Groß, Klein, Zahl, Sonderzeichen)." },
                                { title: "Neu einloggen", desc: "mit dem neuen Passwort — jetzt sind Sie in der App." },
                                { title: "Verlag wählen", desc: "auf der Verlagsauswahl-Seite den gewünschten Verlag anklicken." },
                            ]} />
                        </div>
                        <Tip tone="good">
                            Passwort selbst ändern? Klicken Sie oben rechts auf Ihren Namen → \u201EPasswort ändern". Nach dem
                            Wechsel werden Sie einmalig neu angemeldet.
                        </Tip>
                        <Tip tone="warn">
                            Konto gesperrt? Bei \u201EKonto deaktiviert" bitte Ihren Administrator kontaktieren — der Zugang wurde
                            temporär gesperrt (z. B. während einer längeren Abwesenheit).
                        </Tip>
                    </Section>

                    <Section id="verlagswahl" icon={LayoutDashboard} title="Verlagsauswahl">
                        <p>
                            Nach dem Login sehen Sie alle Verlage, für die Sie freigeschaltet sind. Jede Kachel zeigt Ort und —
                            wenn Sie Administrator sind — Lagerwert, Bilanzwert und die aktuelle Bewertungs-Periode auf einen Blick.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Aktionen auf dieser Seite</div>
                            <ul className="space-y-1.5 list-disc pl-5">
                                <li><strong className="text-foreground">Verlag auswählen:</strong> Klick auf eine Karte → Sie landen in der Übersicht des gewählten Verlags.</li>
                                <li><strong className="text-foreground">Zurück zur Auswahl:</strong> Klick auf das Logo \u201EINVENTUR PRO" oben links in der Sidebar, oder Menü rechts oben → \u201EAnderen Verlag wählen".</li>
                                <li><strong className="text-foreground">Benutzerverwaltung öffnen (nur Admin):</strong> Button oben rechts — mandantenübergreifend, ohne einen bestimmten Verlag aktivieren zu müssen.</li>
                            </ul>
                        </div>
                    </Section>

                    <Section id="uebersicht" icon={LayoutDashboard} title="Übersicht (Dashboard)">
                        <p>
                            Das Dashboard ist Ihr Kontrollzentrum pro Verlag. Es aktualisiert sich live nach jedem Import, jeder
                            HK-Änderung und jeder Sonder-Wertberichtigung.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="rounded-md border border-border/60 bg-surface2 p-3 text-xs">
                                <div className="text-foreground font-medium mb-1">Gesamtlagerwert</div>
                                <span>Summe aller Lagerwerte im gewählten Zeitraum. Formel: Lager Gesamt × verwendeter HK.</span>
                            </div>
                            <div className="rounded-md border border-border/60 bg-surface2 p-3 text-xs">
                                <div className="text-foreground font-medium mb-1">Bilanzwert</div>
                                <span>Lagerwert minus Wertberichtigung. Das ist der Wert, der in Ihrer Bilanz erscheint.</span>
                            </div>
                            <div className="rounded-md border border-border/60 bg-surface2 p-3 text-xs">
                                <div className="text-foreground font-medium mb-1">Erlösgruppen-Verteilung</div>
                                <span>Zeigt, wie sich der Bilanzwert auf Ihre Programmschwerpunkte (z. B. AB, KJ) aufteilt.</span>
                            </div>
                            <div className="rounded-md border border-border/60 bg-surface2 p-3 text-xs">
                                <div className="text-foreground font-medium mb-1">Vorjahresvergleich (Δ)</div>
                                <span>Absolute und prozentuale Veränderung zum Vorjahr — hilft beim Erkennen von Ausreißern.</span>
                            </div>
                        </div>
                        <Tip>
                            <strong>Bewertungszeitraum wechseln:</strong> Über die Jahres-Tabs im Dashboard-Kopf können Sie
                            historische Bewertungen einsehen. Alle Zahlen laden neu.
                        </Tip>
                    </Section>

                    <Section id="import" icon={Upload} title="Excel-Import (Herzstück)">
                        <p>
                            Der Import-Wizard ist der schnellste Weg, ein ganzes Jahr an Bestandsdaten aus Ihrem Verlagssystem
                            in INVENTUR PRO zu übernehmen.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Ablauf (6 Schritte)</div>
                            <Steps items={[
                                { title: "Zeitraum-Typ wählen", desc: "Jahr / Quartal / Monat. Für die klassische Jahresbilanz: Jahr." },
                                { title: "Zeitraum wählen", desc: `z. B. "2025".` },
                                { title: "Excel-Datei hochladen", desc: "Endung .xlsx. Datei muss ISBN und Haupttitel als Pflichtspalten haben." },
                                { title: "Spalten-Zuordnung", desc: "Auto-Mapping ordnet Standardfelder zu; manuell nachjustieren wenn nötig." },
                                { title: "Dry-Run starten", desc: "Simuliert den Import, ohne zu speichern. Zeigt: würde importieren, Datenleichen, 0-Lager-Titel, Fehler." },
                                { title: "Wirklich importieren", desc: "Klick auf den roten Button schreibt die Daten. Toast bestätigt den Erfolg." },
                            ]} />
                        </div>
                        <Tip tone="warn">
                            <strong>0-Lager-Filter:</strong> Zeilen ohne Bestand (Lager Ö = 0 <em>und</em> Lager D = 0) werden übersprungen,
                            <em>außer</em> das ET-Jahr liegt im Fenster {"{Zeitraum-1, Zeitraum, Zeitraum+1}"} — Vorabprodukte und Novitäten
                            bleiben also erhalten.
                        </Tip>
                        <Tip>
                            <strong>Auto-Inaktivierung:</strong> Titel, die in der aktuellen Import-Datei fehlen, werden automatisch als \u201Einaktiv" markiert
                            (nicht gelöscht). Die Historie bleibt erhalten, sie tauchen in aktuellen Bewertungen aber nicht mehr auf.
                        </Tip>
                        <Tip tone="good">
                            <strong>Wiederkehrender Import:</strong> Sie können denselben Zeitraum jederzeit erneut importieren — bestehende
                            Datensätze werden aktualisiert (Upsert), keine Duplikate entstehen. Manuelle Änderungen werden dabei überschrieben.
                        </Tip>
                    </Section>

                    <Section id="inventar" icon={ListTree} title="Inventar & Titelpflege">
                        <p>
                            Die Seite \u201EInventar" listet alle Titel eines Verlags für die gewählte Periode inkl. Bewegungen,
                            berechneten Werten und den Bewertungs-Tags (NE/ND/R/M).
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Filter & Suche</div>
                            <ul className="space-y-1.5 list-disc pl-5">
                                <li><strong className="text-foreground">Status:</strong> Aktiv / Inaktiv / Alle</li>
                                <li><strong className="text-foreground">Erlösgruppe:</strong> filtert auf ein Programm (z. B. AB, KJ, Fachbuch)</li>
                                <li><strong className="text-foreground">Programmplatz:</strong> filtert auf ein Programmhalbjahr</li>
                                <li><strong className="text-foreground">Suche:</strong> ISBN oder Haupttitel-Teile</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Titel bearbeiten</div>
                            <p>Klick auf eine Zeile öffnet den Detail-Dialog. Hier können Sie:</p>
                            <ul className="space-y-1.5 list-disc pl-5 mt-1">
                                <li>HK-Werte (Ist/Kal) ergänzen oder korrigieren</li>
                                <li>Stammdaten überarbeiten</li>
                                <li>Bewegungen einzeln editieren</li>
                                <li>Einen Titel manuell deaktivieren/reaktivieren</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">HK-Pflege in Masse</div>
                            <p>
                                Über den eigenen Menüpunkt \u201EHK-Pflege" ändern Sie mehrere Herstellkosten gleichzeitig — nützlich
                                nach dem Import, um alle Titel ohne HK auf einmal zu ergänzen.
                            </p>
                        </div>
                    </Section>

                    <Section id="bewertung" icon={Calculator} title="Bewertung & WB-Staffel">
                        <p>Die Bewertung eines Titels erfolgt automatisch aus den Bewegungsdaten:</p>
                        <div className="rounded-md border border-tenant/40 bg-tenant/5 px-4 py-3 font-mono text-[13px] leading-6">
                            <div>Lagerwert       = Lager Gesamt × verwendeter HK</div>
                            <div>Wertberichtigung = Lagerwert × WB %</div>
                            <div>Bilanzwert      = Lagerwert − Wertberichtigung</div>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Verwendeter HK</div>
                            <p>Priorität: <strong className="text-foreground">HK Ist</strong> (falls vorhanden), sonst <strong className="text-foreground">HK Kal</strong>. Fehlen beide, taucht der Titel im Fehlerprotokoll (Berichte) auf und muss manuell ergänzt werden.</p>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">WB-Staffel (konfigurierbar unter Einstellungen)</div>
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr className="border-b border-border/60 text-muted-foreground/80 uppercase text-[10px] tracking-widest">
                                        <th className="text-left py-2">Alter des Titels</th>
                                        <th className="text-right py-2">WB %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-border/40"><td className="py-1.5">1. Jahr (ET = Bewertungsjahr)</td><td className="text-right">0 %</td></tr>
                                    <tr className="border-b border-border/40"><td className="py-1.5">2. Jahr</td><td className="text-right">25 %</td></tr>
                                    <tr className="border-b border-border/40"><td className="py-1.5">3. Jahr</td><td className="text-right">50 %</td></tr>
                                    <tr className="border-b border-border/40"><td className="py-1.5">4. Jahr</td><td className="text-right">75 %</td></tr>
                                    <tr><td className="py-1.5">5. Jahr und älter</td><td className="text-right">100 %</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <Tip>
                            Sie können die WB-Prozente <em>pro Verlag</em> anpassen. Das ist wichtig, wenn Ihre Steuerberatung
                            abweichende Vorgaben macht (z. B. mildere Staffel für langlebige Fachbücher).
                        </Tip>
                        <div>
                            <div className="text-foreground font-medium mb-2">Erstauflagen-Datum (ET) und Auflagen-Anlieferung (AA)</div>
                            <p>
                                Diese beiden Datumsfelder steuern die Bewertung. <strong>ET</strong> = wann der Titel erstmalig erschienen ist,
                                <strong> AA</strong> = wann die aktuelle Auflage geliefert wurde. Bei Nachdrucken kann AA neuer sein als ET —
                                die App erkennt das und vergibt automatisch den passenden Tag (siehe Kapitel 9).
                            </p>
                        </div>
                    </Section>

                    <Section id="sonder-wb" icon={PackageSearch} title="Sonder-Wertberichtigung (Ramsch, Makulatur)">
                        <p>
                            Manchmal reicht die reguläre Staffel nicht. Ist ein Titel verramscht oder makuliert, greift eine
                            <strong className="text-foreground"> Sonder-Wertberichtigung</strong>, die die reguläre Staffel überschreibt.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Zwei Sonder-Typen</div>
                            <ul className="space-y-2 list-disc pl-5">
                                <li>
                                    <strong className="text-foreground">Verramschung (Tag \u201ER"):</strong> Titel wurde in den
                                    Abverkauf gegeben. Bilanzwert wird auf einen konfigurierten Restwert reduziert (z. B. 20 % vom Lagerwert).
                                </li>
                                <li>
                                    <strong className="text-foreground">Makulatur (Tag \u201EM"):</strong> Titel wurde vernichtet.
                                    Bilanzwert = 0.
                                </li>
                            </ul>
                        </div>
                        <p>
                            Sonder-WB-Ereignisse legen Sie in \u201ESonder-WB" an — mit ISBN, Datum und Typ. Ab dem Datum wirkt der Effekt
                            in allen künftigen Bewertungen. Historische Snapshots bleiben unverändert.
                        </p>
                    </Section>

                    <Section id="berichte" icon={FileBarChart} title="Berichte & Tags">
                        <p>
                            Die Berichte-Seite zeigt Ihnen Auffälligkeiten und Verteilungen auf einen Blick. Sie ist die
                            Kontroll-Zentrale vor jedem Jahresabschluss.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Auto-Tags</div>
                            <p>Titel werden je Periode automatisch klassifiziert:</p>
                            <ul className="space-y-1.5 list-disc pl-5 mt-1">
                                <li><span className="font-mono text-emerald-300">NE</span> — <strong>Neuerscheinung</strong>: Erstauflage im Bewertungsjahr, oder Vorabtitel für das Folgejahr.</li>
                                <li><span className="font-mono text-blue-300">ND</span> — <strong>Nachdruck</strong>: Auflage ≥ 2 im Bewertungsjahr, oder erneute Anlieferung eines älteren Titels.</li>
                                <li><span className="font-mono text-yellow-300">R</span> — <strong>Ramsch</strong>: Sonder-WB Verramschung greift.</li>
                                <li><span className="font-mono text-red-300">M</span> — <strong>Makulatur</strong>: Sonder-WB Makulatur greift.</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Weitere Bericht-Blöcke</div>
                            <ul className="space-y-1.5 list-disc pl-5">
                                <li><strong className="text-foreground">Fehlerprotokoll:</strong> Titel ohne HK-Werte — müssen manuell ergänzt werden.</li>
                                <li><strong className="text-foreground">Konflikte:</strong> Titel, bei denen NE und ND gleichzeitig gelten würden (fast immer ein Datenfehler).</li>
                                <li><strong className="text-foreground">Datenlücken AA:</strong> siehe eigener Absatz unten.</li>
                                <li><strong className="text-foreground">Excel-Exporte</strong> pro Bericht — direkt für die Steuerberatung.</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Datenlücken AA (Auflagen-Anlieferung) im Detail</div>
                            <p>
                                <strong>AA</strong> = Auflagen-Anlieferung, also das Datum, an dem die aktuelle Auflage im Lager
                                angekommen ist. Der Bericht zählt alle Bewegungen, bei denen <em>kein AA-Datum</em> hinterlegt ist,
                                aber gleichzeitig eine <em>Auflagennummer ≥ 2</em> — also Nachdrucke, deren Anlieferung unklar ist.
                            </p>
                            <p>Warum ist das relevant? Weil die App das AA-Jahr braucht, um zu entscheiden:</p>
                            <table className="w-full text-xs border-collapse mt-2">
                                <thead>
                                    <tr className="border-b border-border/60 text-muted-foreground/80 uppercase text-[10px] tracking-widest">
                                        <th className="text-left py-2">Situation</th>
                                        <th className="text-left py-2">AA-Jahr</th>
                                        <th className="text-left py-2">Ergebnis</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-border/40">
                                        <td className="py-1.5">Auflage 2 im laufenden Jahr angeliefert</td>
                                        <td className="py-1.5">2025</td>
                                        <td className="py-1.5"><span className="font-mono text-blue-300">ND-Tag</span> — Nachdruck 2025, bewertungsrelevant</td>
                                    </tr>
                                    <tr className="border-b border-border/40">
                                        <td className="py-1.5">Auflage 2 vor Jahren angeliefert, Lager-Rest</td>
                                        <td className="py-1.5">2018</td>
                                        <td className="py-1.5">kein ND-Tag — alter Bestand nach normaler Staffel</td>
                                    </tr>
                                    <tr>
                                        <td className="py-1.5">Auflage 2, kein AA-Datum bekannt</td>
                                        <td className="py-1.5 text-yellow-300">fehlt</td>
                                        <td className="py-1.5 text-yellow-300">⚠ Datenlücke — App kann nicht klassifizieren</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Wie Sie eine Datenlücke schließen</div>
                            <Steps items={[
                                { title: `"Datenlücken AA" klicken`, desc: "lädt eine Excel mit allen betroffenen Titeln herunter." },
                                { title: "In Ihrem Verlagssystem nachschauen", desc: "wann die aktuelle Auflage tatsächlich angeliefert wurde." },
                                { title: "Nachtragen im Inventar-Detail", desc: `Titel öffnen → Feld "Aktuelle Auflage: Anliefertermin" ausfüllen → speichern.` },
                                { title: "Optional beim Import beheben", desc: "sicherstellen, dass die Excel-Vorlage die Spalte enthält — dann kommt die Info beim nächsten Import automatisch." },
                            ]} />
                        </div>
                        <Tip tone="warn">
                            <strong>Häufige Ursachen:</strong> Erst-Import ohne AA-Spalte, ältere Titel bei denen der Verlag das Datum nie erfasst hat, oder Datenübernahme aus einem Vorgänger-System, bei der das Feld verloren ging.
                        </Tip>
                        <div className="mt-6 border-t border-border pt-6">
                            <div className="text-foreground font-medium mb-2">Bestands-Roll-Forward (eigener Menüpunkt)</div>
                            <p>
                                Neben den klassischen Berichten gibt es einen eigenen Menüpunkt <strong className="text-foreground">Roll-Forward</strong>, der
                                eine buchhalterische Bestandsveränderungsrechnung nach der Formel <em>Anfangsbestand + Zugänge − Verbrauch = Endbestand</em>
                                erstellt. Ideal für die Frage: <em>Wie viel Wert aus dem Vorjahres-Lager wurde im aktuellen Jahr verbraucht?</em>
                            </p>
                            <div>
                                <div className="text-foreground font-medium mt-3 mb-2">Kategorisierung je Titel</div>
                                <ul className="space-y-1.5 list-disc pl-5">
                                    <li><strong className="text-emerald-300">Neuerscheinung (NE):</strong> Erstauflage im Bewertungsjahr — voller Wert zählt als Zugang.</li>
                                    <li><strong className="text-blue-300">Nachdruck (ND):</strong> Nur der Zuwachs gegenüber Vorjahr zählt als Zugang; der Rest ist verblieben.</li>
                                    <li><strong className="text-slate-300">Aus Vorjahr weitergeführt:</strong> Titel ohne Tag, Bestand kann steigen oder fallen. Fällt er, ist das Verbrauch.</li>
                                    <li><strong className="text-red-300">Komplett abverkauft:</strong> Titel war 2024 aktiv, ist 2025 nicht mehr aktiv — sein voller Vorjahres-Wert ist Verbrauch.</li>
                                    <li><strong className="text-yellow-300">Sonstiger Zugang:</strong> Zuwachs ohne NE/ND-Tag — meist Datenlücke oder Sonderfall.</li>
                                </ul>
                            </div>
                            <div>
                                <div className="text-foreground font-medium mt-3 mb-2">Was Sie in der App sehen</div>
                                <ul className="space-y-1.5 list-disc pl-5">
                                    <li>4 KPI-Kacheln: Anfangsbestand, Verbrauch (Ihre Kernzahl), Zugang gesamt, Endbestand</li>
                                    <li>Waterfall-Balken mit relativer Breite — Sie sehen sofort die Größenordnungen</li>
                                    <li>Kategorien-Kacheln mit Anzahl Titel und Wert je Kategorie</li>
                                    <li>Top-5-Vorschau je Kategorie (welche Titel treiben den größten Zugang/Verbrauch)</li>
                                    <li>Excel-Export: 2 Sheets — Zusammenfassung und vollständige Detail-Liste</li>
                                </ul>
                            </div>
                            <Tip>
                                <strong>Kontrolldifferenz:</strong> Kleine Abweichungen (meist &lt; 2 %) zwischen berechnetem und tatsächlichem Endbestand entstehen durch HK-Änderungen zwischen den Jahren
                                — Vorjahres-Bewertung nutzt den damaligen HK, aktuelle Bewertung den neuen. Für die Aussagekraft der Waterfall unerheblich, wird aber transparent angezeigt.
                            </Tip>
                            <Tip tone="warn">
                                <strong>Was der Verbrauch wirklich enthält:</strong> Da die App keine echten Verkaufsdaten hat, ist Verbrauch eine Restgröße —
                                sie umfasst Verkäufe, Retouren, Verramschung, Makulatur und ggf. Fehlbestandsbuchungen. Für Ihre Steuerberatung ist das üblich und aussagekräftig.
                            </Tip>
                        </div>
                    </Section>

                    <Section id="snapshots" icon={Snowflake} title="Snapshots (Jahresabschluss)">
                        <p>
                            Ein <strong className="text-foreground">Snapshot</strong> friert den Bewertungsstand eines Jahres ein. Sobald Ihre Buchhaltung
                            die Zahlen offiziell verwendet, sollten Sie den Snapshot abschließen — danach ist er unveränderlich, dient als
                            Referenz und wird als offizielle Bilanzgrundlage archiviert.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Workflow</div>
                            <Steps items={[
                                { title: "Snapshot erzeugen", desc: `"Snapshot anlegen" für das gewünschte Jahr. Die App aggregiert die aktuellen Werte.` },
                                { title: "Prüfen", desc: "Bilanzansicht + Bestandsveränderung zum Vorjahr durchgehen." },
                                { title: "Excel/PDF exportieren", desc: "aus dem Snapshot-Dialog. Diese Datei geht an Ihre Steuerberatung." },
                                { title: "Abschließen", desc: `"Abschließen" macht den Snapshot schreibgeschützt. Danach nicht mehr rückgängig.` },
                            ]} />
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Manuelle Baseline (z. B. 2023)</div>
                            <p>
                                Wenn Sie mit INVENTUR PRO in der Mitte eines laufenden Jahres starten, können Sie einen historischen
                                Snapshot manuell einpflegen (Werte aus Ihrer alten Bilanz eintragen) — so hat die Vorjahresvergleich-Ansicht
                                sofort Kontext, ohne dass Sie Altdaten importieren müssen.
                            </p>
                        </div>
                        <Tip tone="warn">
                            <strong>Wichtiger Unterschied:</strong> Snapshots sind für die Buchhaltung. Der Dashboard-Wert dagegen ist
                            <em> live</em> und ändert sich mit jeder HK-Korrektur. Vergleichen Sie beide, um zu prüfen, ob nach Snapshot-Erstellung
                            noch etwas verändert wurde.
                        </Tip>
                    </Section>

                    <Section id="export" icon={FileSpreadsheet} title="Export für Buchhaltung">
                        <p>
                            Alle Exporte, die für Steuerberater oder Finanzamt gedacht sind, finden Sie zentral auf der Seite <strong className="text-foreground">Export</strong>.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Verfügbare Exporte</div>
                            <ul className="space-y-2 list-disc pl-5">
                                <li>
                                    <strong className="text-foreground">Bestandsveränderung</strong> (Excel + PDF)<br />
                                    Zeigt Deltas zwischen zwei aufeinanderfolgenden Jahren (Absolutwerte + Veränderungen). Enthält
                                    die Sheets \u201EBestandsveränderung", \u201EAbsolutwerte" und \u201EStückzahlen".
                                </li>
                                <li>
                                    <strong className="text-foreground">Inventur-Detail (vollständige Titelliste)</strong> (Excel + PDF)<br />
                                    Alle aktiven Titel eines Jahres mit Lager, HK, WB %, Wertberichtigung, Bilanzwert und Tags.
                                    Im Excel <em>ein Tabellenblatt pro Erlösgruppe</em> mit Σ-Zeile — ideal für Steuerberater/Finanzamt.
                                </li>
                            </ul>
                        </div>
                        <Tip tone="good">
                            <strong>Verlagsfarben in den Exports:</strong> Die Header übernehmen die Farbe des jeweiligen Verlags —
                            so wissen Sie immer sofort, zu welchem Mandanten ein ausgedrucktes PDF gehört.
                        </Tip>
                    </Section>

                    <Section id="benutzer" icon={UsersRound} title="Benutzerverwaltung (nur Admin)">
                        <p>
                            Als Administrator legen Sie neue Benutzer an, weisen ihnen Verlage zu und vergeben Rollen. Alle
                            Benutzer sind mandantenübergreifend — dieselbe Person kann in mehreren Verlagen freigeschaltet sein.
                        </p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Rollen</div>
                            <Term term="Administrator" def="Vollzugriff: Benutzer verwalten, alle Verlage sehen, WB konfigurieren, Backups, Reset, Snapshots abschließen." />
                            <Term term="Benutzer" def="Zugriff auf zugewiesene Verlage. Kann Titel und Bewegungen bearbeiten, importieren, Berichte einsehen — keine Benutzer- oder Reset-Funktionen." />
                            <Term term="Gast" def="Nur-Lese-Zugriff auf zugewiesene Verlage. Kann alles einsehen und exportieren, aber nichts ändern. Ideal für Steuerberater/externe Prüfer." />
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Neuen Benutzer anlegen</div>
                            <Steps items={[
                                { title: `"Neuer Benutzer" klicken`, desc: "im Kopf der Benutzerliste." },
                                { title: "Formular ausfüllen", desc: `Name, E-Mail, Rolle, Verlagszugang; Passwort per "Vorschlag generieren" oder eigenes.` },
                                { title: "Muss beim ersten Login geändert werden", desc: "Standard aktiviert — der Nutzer setzt beim ersten Login selbst sein Passwort." },
                                { title: "Zugangsdaten-Dialog", desc: `erscheint einmalig — E-Mail, Passwort kopieren oder direkt per "E-Mail vorbereiten"-Button an den Nutzer senden. Danach ist das Passwort nicht mehr abrufbar!` },
                            ]} />
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Passwort-Regeln</div>
                            <p>Neue und geänderte Passwörter müssen erfüllen:</p>
                            <ul className="space-y-1 list-disc pl-5 mt-1">
                                <li>mindestens 10 Zeichen</li>
                                <li>mindestens ein Großbuchstabe</li>
                                <li>mindestens ein Kleinbuchstabe</li>
                                <li>mindestens eine Ziffer</li>
                                <li>mindestens ein Sonderzeichen</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Aktiv / Deaktiviert</div>
                            <p>
                                Statt einen Benutzer zu löschen: <strong className="text-foreground">deaktivieren</strong> (Power-Icon). Deaktivierte Konten
                                können sich nicht einloggen und laufende Sessions werden sofort beendet — Konto und Historie bleiben aber erhalten. Ideal
                                für Karenz, Urlaub, unklare Fälle. Reaktivieren mit dem gleichen Button.
                            </p>
                        </div>
                        <Tip tone="warn">
                            Der <strong>letzte aktive Administrator</strong> kann weder gelöscht noch deaktiviert werden — sonst wäre die App nicht mehr
                            verwaltbar. Ebenso können Sie sich nicht selbst deaktivieren.
                        </Tip>
                    </Section>

                    <Section id="backup" icon={DatabaseBackup} title="Backup, Restore & Reset">
                        <p>Sicherheits-Netz für Ihren Datenbestand. Bevor Sie kritische Aktionen ausführen, ziehen Sie IMMER ein Backup.</p>
                        <div>
                            <div className="text-foreground font-medium mb-2">Backup ziehen</div>
                            <p>
                                Im Admin-Bereich (Import-Seite → Karte \u201EBackup") laden Sie eine ZIP-Datei herunter, die alle Titel,
                                Bewegungen, Snapshots, WB-Konfiguration und Sonder-WB des aktiven Verlags enthält.
                            </p>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Restore einspielen</div>
                            <p>
                                Über Import-Seite → \u201EAus Backup wiederherstellen": ZIP hochladen → Vorschau ansehen (was würde passieren) →
                                mit \u201ERESTORE" bestätigen. <strong className="text-red-300">Achtung: Bestehende Daten des Ziel-Mandanten werden vollständig ersetzt.</strong>
                            </p>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Reset (Alles löschen)</div>
                            <p>
                                Löscht alle Daten des aktiven Mandanten. Nur nach expliziter mehrstufiger Bestätigung (\u201ERESET" eintippen).
                                Empfohlener Weg vor einem großen Erst-Import, um Demo-Daten zu entfernen.
                            </p>
                        </div>
                        <Tip tone="good">
                            Preview und Production teilen sich dieselbe Datenbank — ein Backup aus Preview funktioniert auch in Production
                            und umgekehrt.
                        </Tip>
                    </Section>

                    <Section id="tipps" icon={Wrench} title="Tipps & Troubleshooting">
                        <div>
                            <div className="text-foreground font-medium mb-2">Häufige Fragen</div>
                            <Term term="Import-Zahlen sehen komisch aus" def="Nutzen Sie den Dry-Run! Er zeigt bereits vor dem Speichern, wie viele Titel importiert, wie viele als Datenleichen verworfen werden und ob Fehler auftreten." />
                            <Term term="Bilanzwert ändert sich unerwartet" def="Meist wurde HK geändert oder eine Sonder-WB angelegt. Der Dashboard-Wert ist live — für offizielle Zahlen den letzten abgeschlossenen Snapshot heranziehen." />
                            <Term term="Titel fehlt im Inventar" def={`Prüfen Sie: (1) Ist der Status-Filter auf "Aktiv" gesetzt? (2) Wurde der Titel bei letzten Import auto-inaktiviert (weil in Datei nicht enthalten)?`} />
                            <Term term="Excel-Format anders als erwartet" def="INVENTUR PRO ist tolerant gegenüber Spalten-Namen. Nutzen Sie im Wizard-Schritt 4 das manuelle Mapping." />
                            <Term term="Zugangsdaten für neuen User verloren" def={`Ein Admin öffnet den Bearbeiten-Dialog des Users → "Passwort zurücksetzen (optional)" → neues PW eingeben oder Vorschlag generieren. Der Nutzer muss es beim nächsten Login wieder ändern.`} />
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Tastatur-Shortcuts</div>
                            <ul className="space-y-1 list-disc pl-5">
                                <li><KBD>Esc</KBD> — schließt Dialoge (außer den erzwungenen Passwort-Dialog)</li>
                                <li><KBD>Ctrl</KBD>+<KBD>C</KBD> im Zugangsdaten-Dialog — funktioniert auch, wenn die Kopieren-Buttons scheitern</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-foreground font-medium mb-2">Support & Rückfragen</div>
                            <p>
                                Diese App wurde speziell auf Ihre Verlagsprozesse zugeschnitten. Bei Änderungswünschen,
                                neuen Berichten oder Feature-Ideen wenden Sie sich an Ihren Ansprechpartner — die App wird laufend
                                weiterentwickelt.
                            </p>
                        </div>
                    </Section>

                    <Section id="demo-zugaenge" icon={ShieldCheck} title="Anhang: Demo-Zugänge">
                        <p>Für Trainings- und Testzwecke stehen drei vorkonfigurierte Konten bereit:</p>
                        <div className="rounded-md border border-border/60 bg-surface2 p-4 font-mono text-xs space-y-1">
                            <div><span className="text-tenant">admin@inventur.pro</span> / Admin#2026 — Administrator</div>
                            <div><span className="text-tenant">benutzer@inventur.pro</span> / Benutzer#2026 — Benutzer</div>
                            <div><span className="text-tenant">gast@inventur.pro</span> / Gast#2026 — Gast (nur Lesezugriff)</div>
                        </div>
                        <Tip tone="warn">
                            Ändern Sie diese Passwörter in Ihrer Produktivumgebung! Die Standardwerte sind öffentlich bekannt.
                        </Tip>
                    </Section>

                    <div className="pt-4 flex items-center gap-2 text-xs text-muted-foreground/70">
                        <ArrowLeftRight className="h-3 w-3" />
                        <span>Ende des Handbuchs. Über das Inhaltsverzeichnis oben können Sie jederzeit zurückspringen.</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
